import * as React from 'react'

import { LoginHeading } from '@ally/ally-login'
import {
  Box,
  Button,
  RadioButton,
  RadioGroup,
  TextBody,
  upToMd,
} from '@ally/metronome-ui'
import { AsyncStatus } from '@ally/transmitigator'

import { useTransmit } from '../TransmitContext'
import { TrackingEvents } from '../constants'

const YesLabel: React.FC = () => (
  <TextBody size="sm" tag="span">
    Yes - this is a&nbsp;<strong>trusted computer or device</strong>&nbsp;(like
    a home or personal work computer)
  </TextBody>
)

const NoLabel: React.FC = () => (
  <TextBody size="sm" tag="span">
    No - this is a&nbsp;<strong> shared/public computer or device </strong>
    &nbsp;(like a friend&#39;s or those in a library)
  </TextBody>
)

interface RegisterDeviceContentProps {
  status: AsyncStatus
  isInPortal: boolean | undefined
  onComplete: (register: 'Yes' | 'No') => void
}

export const RegisterDeviceContent: React.FC<RegisterDeviceContentProps> = ({
  status,
  isInPortal,
  onComplete,
}) => {
  const [isRegistering, setIsRegistering] = React.useState<'Yes' | 'No'>('Yes')
  const { logger, hostServices } = useTransmit()

  React.useEffect(() => {
    logger?.track(TrackingEvents.TransmitRegisterDeviceLoad)

    hostServices?.analytics.trackPageView({
      eventType: 'pageview',
      pageName: 'AOS:Login:Register Device',
    })
  }, [])

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault()
    logger?.track(TrackingEvents.TransmitRegisterDeviceSubmit)
    onComplete(isRegistering)
  }

  const handleChange = (selected: string): void => {
    setIsRegistering(selected as 'Yes' | 'No')
  }

  return (
    <>
      <LoginHeading title="Register this device?" isInPortal={isInPortal} />
      <Box mb="md" display="flex" width={upToMd(12, 6)}>
        <TextBody size="sm" tag="p">
          Come here often? If this is a personal device, we can remember it for
          next time, and you won&#39;t need a security code to log in. You
          should only do this for devices you trust, not for shared or public
          ones.
        </TextBody>
      </Box>
      <form onSubmit={handleSubmit} method="post">
        <Box my={upToMd(2, 1)}>
          <RadioGroup
            legendText="Do you want to register this device?"
            isLegendHidden
            name="registerDevice"
            data-allytmfn="registerDevice"
            defaultValue="Yes"
            onChange={handleChange}
          >
            <RadioButton label={<YesLabel />} value="Yes" />
            <RadioButton label={<NoLabel />} value="No" />
          </RadioGroup>
        </Box>
        <Box my="md">
          <Button
            type="submit"
            variant="primary"
            text="Continue"
            loading={status === 'Loading'}
          />
        </Box>
      </form>
    </>
  )
}

export const RegisterDevice: React.FC = () => {
  const { activeScreen, isInPortal } = useTransmit()

  if (activeScreen.type !== 'RegisterDevice') {
    return null
  }

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <RegisterDeviceContent {...activeScreen.payload} isInPortal={isInPortal} />
  )
}
